import React from 'react';
import { useTranslation } from 'react-i18next';
import { ProfileFormData, FormErrors } from '../types';
import CountryCodeSelectorStandalone from './CountryCodeSelectorStandalone';
import { IconDropDownArrow } from '../../Icons';

interface ProfileStep1Props {
	formData: ProfileFormData;
	errors: FormErrors;
	onFieldChange: (field: keyof ProfileFormData, value: string) => void;
}

const COUNTRIES = [
	{ value: 'AG', label: 'Antigua y Barbuda' },
	{ value: 'AR', label: 'Argentina' },
	{ value: 'AW', label: 'Aruba' },
	{ value: 'BS', label: 'Bahamas' },
	{ value: 'BB', label: 'Barbados' },
	{ value: 'BZ', label: 'Belice' },
	{ value: 'BO', label: 'Bolivia' },
	{ value: 'BQ', label: 'Bonaire (Países Bajos)' },
	{ value: 'BR', label: 'Brasil' },
	{ value: 'CA', label: 'Canadá' },
	{ value: 'CL', label: 'Chile' },
	{ value: 'CO', label: 'Colombia' },
	{ value: 'CR', label: 'Costa Rica' },
	{ value: 'CU', label: 'Cuba' },
	{ value: 'CW', label: 'Curazao (Países Bajos)' },
	{ value: 'DM', label: 'Dominica' },
	{ value: 'EC', label: 'Ecuador' },
	{ value: 'SV', label: 'El Salvador' },
	{ value: 'ES', label: 'España' },
	{ value: 'US', label: 'Estados Unidos' },
	{ value: 'GD', label: 'Granada' },
	{ value: 'GL', label: 'Groenlandia (Reino de Dinamarca)' },
	{ value: 'GT', label: 'Guatemala' },
	{ value: 'GF', label: 'Guayana Francesa' },
	{ value: 'GQ', label: 'Guinea Ecuatorial' },
	{ value: 'GY', label: 'Guyana' },
	{ value: 'HT', label: 'Haití' },
	{ value: 'HN', label: 'Honduras' },
	{ value: 'IN', label: 'India' },
	{ value: 'KY', label: 'Islas Caimán (Reino Unido)' },
	{ value: 'TC', label: 'Islas Turcas y Caicos (Reino Unido)' },
	{ value: 'VG', label: 'Islas Vírgenes Británicas (Reino Unido)' },
	{ value: 'JM', label: 'Jamaica' },
	{ value: 'MQ', label: 'Martinica' },
	{ value: 'MX', label: 'México' },
	{ value: 'NI', label: 'Nicaragua' },
	{ value: 'PA', label: 'Panamá' },
	{ value: 'PY', label: 'Paraguay' },
	{ value: 'PE', label: 'Perú' },
	{ value: 'PR', label: 'Puerto Rico' },
	{ value: 'DO', label: 'República Dominicana' },
	{ value: 'KN', label: 'San Cristóbal y Nieves' },
	{ value: 'VC', label: 'San Vicente y las Granadinas' },
	{ value: 'LC', label: 'Santa Lucía' },
	{ value: 'SR', label: 'Surinam' },
	{ value: 'TT', label: 'Trinidad y Tobago' },
	{ value: 'UY', label: 'Uruguay' },
	{ value: 'VE', label: 'Venezuela' },
];

const ProfileStep1: React.FC<ProfileStep1Props> = ({ formData, errors, onFieldChange }) => {
	const { t } = useTranslation();

	return (
		<>
			<div className="row text-profile__row">
				<div className="col-12 col-lg-6 mt-1">
					<div className="label-container">
						<input
							type="text"
							name="nameValue"
							className="textbox-general"
							value={formData.nameValue}
							onChange={(e) => onFieldChange('nameValue', e.target.value)}
						/>
						<label className="label-input">{t('register.testProfile.step_1.name.placeholder')}</label>
						{errors.nameValue && <div className="error-message-validation">{errors.nameValue}</div>}
					</div>
				</div>
				<div className="col-12 col-lg-6 mt-1">
					<div className="label-container">
						<input
							type="text"
							name="lastNameValue"
							className="textbox-general"
							value={formData.lastNameValue}
							onChange={(e) => onFieldChange('lastNameValue', e.target.value)}
						/>
						<label className="label-input">{t('register.testProfile.step_1.lastname.placeholder')}</label>
						{errors.lastNameValue && <div className="error-message-validation">{errors.lastNameValue}</div>}
					</div>
				</div>
			</div>

			<div className="row text-profile__row">
				<div className="col-12 col-lg-6 mt-1">
					<CountryCodeSelectorStandalone
						countryCode={formData.countryCode}
						mobileNumber={formData.mobileNumber}
						onCountryCodeChange={(value) => onFieldChange('countryCode', value || '')}
						onMobileNumberChange={(value) => onFieldChange('mobileNumber', value)}
						error={errors.mobileNumber}
					/>
				</div>
				<div className="col-12 col-lg-6 mt-1">
					<div className="label-container">
						<label className="label-input">{t('register.testProfile.step_1.country.placeholder')}</label>
						<select
							className="label-container__input input-label-select"
							name="country"
							value={formData.country}
							onChange={(e) => onFieldChange('country', e.target.value)}>
							<option value="">{t('select.placeholder')}</option>
							{COUNTRIES.map((country) => (
								<option
									key={country.value}
									value={country.value}>
									{country.label}
								</option>
							))}
						</select>
						{errors.country && <div className="error-message-validation">{errors.country}</div>}
						<IconDropDownArrow className="input-label-select-arrow" />
					</div>
				</div>
			</div>

			<div className="row text-profile__row">
				<div className="col-12 col-lg-6 mt-1">
					<div className="label-container">
						<input
							type="text"
							name="companyValue"
							className="textbox-general"
							value={formData.companyValue}
							onChange={(e) => onFieldChange('companyValue', e.target.value)}
						/>
						<label className="label-input">{t('register.testProfile.step_1.company.placeholder')}</label>
					</div>
				</div>
				<div className="col-12 col-lg-6 mt-1">
					<div className="label-container">
						<input
							type="text"
							name="occupationValue"
							className="textbox-general"
							value={formData.occupationValue}
							onChange={(e) => onFieldChange('occupationValue', e.target.value)}
						/>
						<label className="label-input">{t('register.testProfile.step_1.position.placeholder')}</label>
					</div>
				</div>
			</div>
		</>
	);
};

export default ProfileStep1;
